import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Bem from 'bemHelper';
import numDesc from 'helpers/numDesc';
import { I18nHoc } from 'helpers/i18n';
import { authConnect, UserProp } from 'auth';
import DataProvider from 'api/data-provider';
import Button from 'components/button';
import Link from 'components/link';
import cacheTerms from 'helpers/cacheTerms';

import List from './list';
import Editor from './editor';
import {
  BACKEND_LIMIT,
  DEFAULT_COMMENT_LIMIT,
  LIST_URL,
  FORM_CONTROLLER
} from './config';

import translates from './comments-i18n.json';

import 'styles/base/subsys/comments/_comments.scss';

const bemClasses = new Bem('comments');

/**
 * Workaround for implement cache-friendly behavior.
 * We need to force reload comments after page load
 * with preloader, but don't need to this reload when client works in SPA mode
 */
let clientFirstLoad = true;

@authConnect()
@I18nHoc(translates)
class Comments extends Component {
  static propTypes = {
    // Comment entity
    entity: PropTypes.string.isRequired,
    // Entity ID
    entityId: PropTypes.number.isRequired,
    // Comment list title
    title: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node
    ]),
    // CSS class name
    className: PropTypes.string,
    // Limit to comments output
    limit: PropTypes.number,
    // Show all comments
    full: PropTypes.bool,
    // Comments data provider's URL (in standart cases redefine is not needed)
    listUrl: PropTypes.string,
    // Comments data provider's form controller (in standart cases redefine is not needed)
    formController: PropTypes.string,
    // Preloaded comments, if preloader was used
    preloaded: PropTypes.shape({
      count: PropTypes.number
    }),
    // If this prop is set - button "show all comments" will be link to the page with all comments
    // otherwise - this button expands a comments list
    linkToAllComments: PropTypes.string,
    auth: UserProp,
    checkPermission: PropTypes.func,
    actionDoLogin: PropTypes.func,
    subscribe: PropTypes.bool,
    noInteraction: PropTypes.bool,
    noVisitingCard: PropTypes.bool,
    updateEvent: PropTypes.string,
    topic: PropTypes.string,
    autoHideReply: PropTypes.bool,
    showCountNewComments: PropTypes.bool,
  };

  static defaultProps = {
    limit: DEFAULT_COMMENT_LIMIT,
    listUrl: LIST_URL,
    formController: FORM_CONTROLLER,
    subscribe: true,
    autoHideReply: true,
    showCountNewComments: true,
    noVisitingCard: false
  };

  constructor(props, context) {
    super(props, context);
    this.fullRefresh = this.fullRefresh.bind(this);
    this.handleLoaded = this.handleLoaded.bind(this);
    this.jumpToId = null;
    this.providerRef = null;
    this.state = {
      limit: props.full ? BACKEND_LIMIT : props.limit,
      count: props.preloaded ? props.preloaded.count : 0
    };
  }

  componentDidMount() {
    if (clientFirstLoad && this.props.preloaded) {
      this.providerRef.reload();
      clientFirstLoad = false;
    }
  }

  _setProviderRef = el => this.providerRef = el;

  fullRefresh(data = null) {
    // Expand if collapsed
    if (
      this.state.limit !== BACKEND_LIMIT &&
      this.state.count > this.props.limit
    ) {
      this.setState({ limit: BACKEND_LIMIT });
      return;
    }

    if (data) {
      this.jumpToId = data.id;
    }
  }

  jump(id) {
    this.list.jump(id);
  }

  handleLoaded(data, metadata) {
    this.setState({ count: metadata.commentsCount });
    if (this.jumpToId) {
      this.jump(this.jumpToId);
      this.jumpToId = null;
    }
  }

  render() {
    const { i18n } = this;
    const {
      entity, entityId, title, full, preloaded,
      auth, checkPermission, actionDoLogin, listUrl, formController, noInteraction, noVisitingCard,
      className, updateEvent = '', linkToAllComments, subscribe, topic, autoHideReply,
      showCountNewComments
    } = this.props;
    const countMoreComments = this.state.count - DEFAULT_COMMENT_LIMIT;
    const textMoreComments = (
      i18n('commentsShowMore') + numDesc(
        countMoreComments, i18n('commentCount'), i18n('commentCountDeclination'), i18n('commentCountMultiple')
      )
    );
    const otherProps = {};
    if (topic) otherProps.topic = topic;
    if (showCountNewComments) otherProps.showCountNewComments = topic;

    return (
      <div {...bemClasses({ extra: className })}>
        <Editor
          entity={entity}
          entityId={entityId}
          auth={auth}
          actionDoLogin={actionDoLogin}
          checkPermission={checkPermission}
          onCommentSubmit={this.fullRefresh}
          formController={formController}
          addMsg={null}
          updateEvent="add"
          updateCustomEvent={updateEvent}
          resetAfterSubmit
          autoFocus={false}
          subscribe={subscribe}
          {...otherProps}
        />
        {/*
        {!checkPermission(Perms.COMMENTS_POST) && 'Вы не можете писать комментарии'}
        */}
        <div {...bemClasses('listComments')}>
          <DataProvider
            url={listUrl}
            cache={auth.authenticated ? null : cacheTerms.commentsList}
            preloaded={preloaded}
            query={{
              entity,
              entityId,
              limit: full ? BACKEND_LIMIT : this.state.limit,
              count: true,
              $auth: auth.authenticated
            }}
            injectPropName="comments"
            updateEvent={`comment::${entity}::add`}
            onLoaded={this.handleLoaded}
            ref={this._setProviderRef}
          >
            <List
              ref={el => this.list = el}
              entityId={entityId}
              entity={entity}
              auth={auth}
              title={title}
              expandComments={() => this.setState({ limit: BACKEND_LIMIT })}
              formController={formController}
              fullRefresh={this.fullRefresh}
              noInteraction={noInteraction}
              noVisitingCard={noVisitingCard}
              subscribeList={subscribe}
              autoHideReply={autoHideReply}
              {...otherProps}
            />
          </DataProvider>
        </div>
        {(this.state.limit !== BACKEND_LIMIT &&
          this.state.count > this.props.limit) &&
          <div {...bemClasses('footer')}>
            {linkToAllComments &&
              <Link
                {...bemClasses('link', 'moreComments')}
                title={textMoreComments}
                href={linkToAllComments}
              >
                <span {...bemClasses('linkContent')}>
                  <span {...bemClasses('text')} children={textMoreComments} />
                </span>
              </Link>
            }
            {!linkToAllComments &&
              <Button
                {...bemClasses('btn', 'moreComments')}
                onClick={() => this.setState({ limit: BACKEND_LIMIT })}
                label={textMoreComments}
                title={textMoreComments}
                noPadding
              />
            }
          </div>
        }
      </div>
    );
  }
}

export default Comments;
